/**
 * Audio feedback module — synthesised card sounds using the Web Audio API.
 *
 * Generates card-handling sounds at runtime using filtered white noise
 * and short oscillator bursts. No audio files are needed — sounds are
 * pre-rendered into AudioBuffers via OfflineAudioContext at startup,
 * then peak-normalised so relative volume is controlled by a single
 * per-sound volume map.
 *
 * Respects the iOS "Ambient" audio session (via cordova-allow-background-audio)
 * so sounds mix with the user's currently-playing music.
 *
 * On iOS, the AudioContext must be resumed from within a user gesture handler.
 * Call `unlockAudio()` on the first touch to satisfy this requirement.
 *
 * Usage:
 *   import { playSound, unlockAudio } from './audioFunctions'
 *   playSound('lift')           // on card pickup
 *   playSound('place')          // on card drop
 *   playSound('draw')           // on stock tap
 *   playSound('flip')           // on face-down card reveal
 *   playSound('autoFoundation') // on double-tap to foundation
 *   playSound('undo')           // on undo
 *   playSound('recycle')        // on waste → stock recycle
 *   playSound('shuffle')        // on new game deal
 *   playSound('autocomplete')   // on autocomplete start
 *   playSound('win')            // on victory
 */

import { appState } from './state'

// ─── Types ──────────────────────────────────────────────────────────────────

type SoundName =
    | 'lift'
    | 'place'
    | 'draw'
    | 'flip'
    | 'autoFoundation'
    | 'undo'
    | 'recycle'
    | 'shuffle'
    | 'autocomplete'
    | 'win'

// ─── Module state ───────────────────────────────────────────────────────────

let _ctx: AudioContext | null = null
const _buffers: Map<SoundName, AudioBuffer> = new Map()
let _unlocked = false

/**
 * Automated test runs should be silent.
 * - `?test=...` is used by the in-app scenario runner.
 * - `navigator.webdriver` is true under browser automation (Playwright/WebDriver).
 */
function isAutomatedTestMode(): boolean {
    if (typeof window === 'undefined') return false

    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('test') !== null || navigator.webdriver === true
}

// ─── Per-sound volume levels ────────────────────────────────────────────────
// Applied at playback time after peak-normalisation to 1.0.

const _volumes: Record<SoundName, number> = {
    lift: 0.75, // very short — needs higher volume to register
    place: 0.35,
    draw: 0.35,
    flip: 0.4, // short — slight bump
    autoFoundation: 0.3,
    undo: 0.3, // longer duration — slightly lower
    recycle: 0.35,
    shuffle: 0.4, // bumped slightly
    autocomplete: 0.35,
    win: 0.35,
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Create a white-noise AudioBuffer of the given duration. */
function createNoiseBuffer(offCtx: OfflineAudioContext, duration: number): AudioBuffer {
    const length = Math.ceil(offCtx.sampleRate * duration)
    const buffer = offCtx.createBuffer(1, length, offCtx.sampleRate)
    const data = buffer.getChannelData(0)
    for (let i = 0; i < length; i++) {
        data[i] = Math.random() * 2 - 1
    }
    return buffer
}

/** Scale an AudioBuffer so its peak amplitude is 1.0. */
function normalizeBuffer(audioBuffer: AudioBuffer): AudioBuffer {
    const data = audioBuffer.getChannelData(0)
    let peak = 0
    for (let i = 0; i < data.length; i++) {
        const abs = Math.abs(data[i])
        if (abs > peak) peak = abs
    }
    if (peak > 0 && peak !== 1) {
        const scale = 1.0 / peak
        for (let i = 0; i < data.length; i++) {
            data[i] *= scale
        }
    }
    return audioBuffer
}

// ─── Sound synthesis ────────────────────────────────────────────────────────

/** 1. PICK UP — soft click/tap, like fingernail tapping a card */
async function synthesizeLift(): Promise<AudioBuffer> {
    const dur = 0.025,
        sr = 44100
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr)
    const n = off.createBufferSource()
    n.buffer = createNoiseBuffer(off, dur)
    const bp = off.createBiquadFilter()
    bp.type = 'bandpass'
    bp.frequency.value = 2800
    bp.Q.value = 1.5
    const g = off.createGain()
    g.gain.setValueAtTime(0.35, 0)
    g.gain.exponentialRampToValueAtTime(0.001, 0.015)
    n.connect(bp).connect(g).connect(off.destination)
    n.start(0)
    return off.startRendering()
}

/** 2. PLACE — crisp tap, no bass */
async function synthesizePlace(): Promise<AudioBuffer> {
    const dur = 0.07,
        sr = 44100
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr)
    const n = off.createBufferSource()
    n.buffer = createNoiseBuffer(off, dur)
    const bp = off.createBiquadFilter()
    bp.type = 'bandpass'
    bp.frequency.value = 2400
    bp.Q.value = 0.8
    const g = off.createGain()
    g.gain.setValueAtTime(0, 0)
    g.gain.linearRampToValueAtTime(0.35, 0.002)
    g.gain.exponentialRampToValueAtTime(0.001, dur)
    n.connect(bp).connect(g).connect(off.destination)
    n.start(0)
    return off.startRendering()
}

/** 3. DRAW FROM STOCK — crisp card snap */
async function synthesizeDraw(): Promise<AudioBuffer> {
    const dur = 0.07,
        sr = 44100
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr)
    const n = off.createBufferSource()
    n.buffer = createNoiseBuffer(off, dur)
    const bp = off.createBiquadFilter()
    bp.type = 'bandpass'
    bp.frequency.value = 3200
    bp.Q.value = 1.0
    const g = off.createGain()
    g.gain.setValueAtTime(0, 0)
    g.gain.linearRampToValueAtTime(0.4, 0.002)
    g.gain.exponentialRampToValueAtTime(0.001, dur)
    n.connect(bp).connect(g).connect(off.destination)
    n.start(0)
    return off.startRendering()
}

/** 4. FLIP CARD — quick rising flick, like cardboard snapping over */
async function synthesizeFlip(): Promise<AudioBuffer> {
    const dur = 0.04,
        sr = 44100
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr)
    const n = off.createBufferSource()
    n.buffer = createNoiseBuffer(off, dur)
    const bp = off.createBiquadFilter()
    bp.type = 'bandpass'
    bp.frequency.setValueAtTime(1800, 0)
    bp.frequency.exponentialRampToValueAtTime(5000, dur * 0.6)
    bp.Q.value = 1.0
    const g = off.createGain()
    g.gain.setValueAtTime(0, 0)
    g.gain.linearRampToValueAtTime(0.35, 0.002)
    g.gain.exponentialRampToValueAtTime(0.001, dur)
    n.connect(bp).connect(g).connect(off.destination)
    n.start(0)
    return off.startRendering()
}

/** 5. AUTO-MOVE TO FOUNDATION — quick swoosh + soft landing thump */
async function synthesizeAutoFoundation(): Promise<AudioBuffer> {
    const dur = 0.12,
        sr = 44100
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr)
    // Swoosh
    const n = off.createBufferSource()
    n.buffer = createNoiseBuffer(off, dur)
    const hp = off.createBiquadFilter()
    hp.type = 'highpass'
    hp.frequency.setValueAtTime(1500, 0)
    hp.frequency.exponentialRampToValueAtTime(4000, 0.05)
    const ng = off.createGain()
    ng.gain.setValueAtTime(0, 0)
    ng.gain.linearRampToValueAtTime(0.2, 0.005)
    ng.gain.exponentialRampToValueAtTime(0.001, 0.07)
    n.connect(hp).connect(ng).connect(off.destination)
    // Landing thump
    const osc = off.createOscillator()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(120, 0.05)
    osc.frequency.exponentialRampToValueAtTime(50, 0.1)
    const og = off.createGain()
    og.gain.setValueAtTime(0, 0)
    og.gain.setValueAtTime(0.2, 0.05)
    og.gain.exponentialRampToValueAtTime(0.001, 0.12)
    osc.connect(og).connect(off.destination)
    n.start(0)
    osc.start(0)
    return off.startRendering()
}

/** 6. UNDO — place sound in reverse (swell up then cut) */
async function synthesizeUndo(): Promise<AudioBuffer> {
    const dur = 0.1,
        sr = 44100
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr)
    const n = off.createBufferSource()
    n.buffer = createNoiseBuffer(off, dur)
    const bp = off.createBiquadFilter()
    bp.type = 'bandpass'
    bp.frequency.setValueAtTime(800, 0)
    bp.frequency.exponentialRampToValueAtTime(2200, dur * 0.8)
    bp.Q.value = 0.6
    const g = off.createGain()
    g.gain.setValueAtTime(0.001, 0)
    g.gain.exponentialRampToValueAtTime(0.35, dur * 0.75)
    g.gain.linearRampToValueAtTime(0, dur)
    n.connect(bp).connect(g).connect(off.destination)
    n.start(0)
    return off.startRendering()
}

/** 7. RECYCLE WASTE — soft gather, multiple tiny clicks close together */
async function synthesizeRecycle(): Promise<AudioBuffer> {
    const dur = 0.2,
        sr = 44100
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr)
    for (let i = 0; i < 5; i++) {
        const t = i * 0.035
        const clickDur = 0.02
        const n = off.createBufferSource()
        n.buffer = createNoiseBuffer(off, clickDur)
        const bp = off.createBiquadFilter()
        bp.type = 'bandpass'
        bp.frequency.value = 1800 + i * 200
        bp.Q.value = 0.6
        const g = off.createGain()
        g.gain.setValueAtTime(0, t)
        g.gain.linearRampToValueAtTime(0.2, t + 0.002)
        g.gain.exponentialRampToValueAtTime(0.001, t + clickDur)
        n.connect(bp).connect(g).connect(off.destination)
        n.start(t)
    }
    return off.startRendering()
}

/** 8. SHUFFLE — card riffle */
async function synthesizeShuffle(): Promise<AudioBuffer> {
    const dur = 0.45,
        sr = 44100
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr)
    const numClicks = 14
    for (let i = 0; i < numClicks; i++) {
        const t = (i / numClicks) * (dur - 0.04) + Math.random() * 0.008
        const clickDur = 0.012 + Math.random() * 0.008
        const n = off.createBufferSource()
        n.buffer = createNoiseBuffer(off, clickDur)
        const bp = off.createBiquadFilter()
        bp.type = 'bandpass'
        bp.frequency.value = 2200 + Math.random() * 1200
        bp.Q.value = 0.7 + Math.random() * 0.6
        const progress = i / numClicks
        const envelope = 0.6 + 0.4 * Math.sin(progress * Math.PI)
        const peakVol = 0.18 * envelope
        const g = off.createGain()
        g.gain.setValueAtTime(0, t)
        g.gain.linearRampToValueAtTime(peakVol, t + 0.001)
        g.gain.exponentialRampToValueAtTime(0.001, t + clickDur)
        n.connect(bp).connect(g).connect(off.destination)
        n.start(t)
    }
    return off.startRendering()
}

/** 9. AUTOCOMPLETE — accelerating series of soft taps */
async function synthesizeAutocomplete(): Promise<AudioBuffer> {
    const dur = 1.2,
        sr = 44100
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr)
    const numCards = 20
    for (let i = 0; i < numCards; i++) {
        const progress = i / numCards
        const t = progress * progress * (dur - 0.1)
        const clickDur = 0.025
        const n = off.createBufferSource()
        n.buffer = createNoiseBuffer(off, clickDur)
        const bp = off.createBiquadFilter()
        bp.type = 'bandpass'
        bp.frequency.value = 2000 + progress * 1500
        bp.Q.value = 0.8
        const g = off.createGain()
        const vol = 0.12 + progress * 0.12
        g.gain.setValueAtTime(0, t)
        g.gain.linearRampToValueAtTime(vol, t + 0.002)
        g.gain.exponentialRampToValueAtTime(0.001, t + clickDur)
        n.connect(bp).connect(g).connect(off.destination)
        n.start(t)
    }
    // Final soft thump
    const osc = off.createOscillator()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(100, dur - 0.1)
    osc.frequency.exponentialRampToValueAtTime(40, dur)
    const og = off.createGain()
    og.gain.setValueAtTime(0, dur - 0.1)
    og.gain.linearRampToValueAtTime(0.15, dur - 0.08)
    og.gain.exponentialRampToValueAtTime(0.001, dur)
    osc.connect(og).connect(off.destination)
    osc.start(dur - 0.1)
    return off.startRendering()
}

/** 10. WIN — confetti popper: pop burst + sparkle rain */
async function synthesizeWin(): Promise<AudioBuffer> {
    const dur = 1.8,
        sr = 44100
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr)

    // Pop — short percussive burst
    const pop = off.createBufferSource()
    pop.buffer = createNoiseBuffer(off, 0.06)
    const popBP = off.createBiquadFilter()
    popBP.type = 'bandpass'
    popBP.frequency.setValueAtTime(3000, 0)
    popBP.frequency.exponentialRampToValueAtTime(800, 0.04)
    popBP.Q.value = 0.5
    const popG = off.createGain()
    popG.gain.setValueAtTime(0.5, 0)
    popG.gain.exponentialRampToValueAtTime(0.001, 0.06)
    pop.connect(popBP).connect(popG).connect(off.destination)
    pop.start(0)

    // Thump underneath the pop
    const thump = off.createOscillator()
    thump.type = 'sine'
    thump.frequency.setValueAtTime(150, 0)
    thump.frequency.exponentialRampToValueAtTime(40, 0.08)
    const tg = off.createGain()
    tg.gain.setValueAtTime(0.2, 0)
    tg.gain.exponentialRampToValueAtTime(0.001, 0.08)
    thump.connect(tg).connect(off.destination)
    thump.start(0)

    // Confetti scatter — bright noise that blooms out from the pop
    const scatter = off.createBufferSource()
    scatter.buffer = createNoiseBuffer(off, dur)
    const scatterHP = off.createBiquadFilter()
    scatterHP.type = 'highpass'
    scatterHP.frequency.setValueAtTime(4000, 0)
    scatterHP.frequency.exponentialRampToValueAtTime(8000, 0.5)
    const sg = off.createGain()
    sg.gain.setValueAtTime(0, 0)
    sg.gain.linearRampToValueAtTime(0.07, 0.05)
    sg.gain.setValueAtTime(0.07, 0.2)
    sg.gain.exponentialRampToValueAtTime(0.02, 0.8)
    sg.gain.exponentialRampToValueAtTime(0.001, dur)
    scatter.connect(scatterHP).connect(sg).connect(off.destination)
    scatter.start(0)

    return off.startRendering()
}

// ─── Initialization ─────────────────────────────────────────────────────────

/** Synthesise a sound and store its peak-normalised buffer. */
async function synthesizeAndStore(name: SoundName, fn: () => Promise<AudioBuffer>): Promise<void> {
    const raw = await fn()
    _buffers.set(name, normalizeBuffer(raw))
}

/**
 * Create the AudioContext and synthesise all sounds into buffers.
 * Called once at startup from index.ts. Synthesis is async but fire-and-forget;
 * sounds will simply be silent until their buffer is ready.
 */
export function initiateAudio(): void {
    if (isAutomatedTestMode()) {
        return
    }

    try {
        _ctx = new (
            window.AudioContext ||
            (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
        )()
    } catch {
        console.warn('Web Audio API not available')
        return
    }

    // Pre-render all sounds into peak-normalised AudioBuffers
    synthesizeAndStore('lift', synthesizeLift)
    synthesizeAndStore('place', synthesizePlace)
    synthesizeAndStore('draw', synthesizeDraw)
    synthesizeAndStore('flip', synthesizeFlip)
    synthesizeAndStore('autoFoundation', synthesizeAutoFoundation)
    synthesizeAndStore('undo', synthesizeUndo)
    synthesizeAndStore('recycle', synthesizeRecycle)
    synthesizeAndStore('shuffle', synthesizeShuffle)
    synthesizeAndStore('autocomplete', synthesizeAutocomplete)
    synthesizeAndStore('win', synthesizeWin)
}

// ─── iOS Audio Unlock ───────────────────────────────────────────────────────

/**
 * Resume the AudioContext on iOS.
 * Must be called from within a user gesture handler (touchstart / mousedown).
 * iOS suspends AudioContext by default until a user gesture triggers `.resume()`.
 */
export function unlockAudio(): void {
    if (isAutomatedTestMode()) {
        return
    }

    if (_unlocked || !_ctx) return
    if (_ctx.state === 'suspended') {
        _ctx.resume()
            .then(() => {
                _unlocked = true
            })
            .catch(() => {
                // Will retry on next gesture
            })
    } else {
        _unlocked = true
    }
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Play a sound effect by name.
 * Respects the `soundEnabled` setting — silently no-ops when disabled.
 * Each call creates a new BufferSourceNode (they are one-shot and disposable).
 * Volume is controlled by the per-sound _volumes map.
 */
export function playSound(name: SoundName): void {
    if (isAutomatedTestMode()) return
    if (!appState.settings.soundEnabled) return
    if (!_ctx || _ctx.state !== 'running') return

    const buffer = _buffers.get(name)
    if (!buffer) return

    const source = _ctx.createBufferSource()
    source.buffer = buffer
    const gain = _ctx.createGain()
    gain.gain.value = _volumes[name]
    source.connect(gain).connect(_ctx.destination)
    source.start(0)
}
